import { userFacingMessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/channel-utils";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { UserFacingMessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message-channels";
import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import ChannelsIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { ArrayStringTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import type { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { MessageChannelIcons } from "../ui/thread-channel-ui";

/** All channels that can appear in a Support v2 ticket. */
const ALL_FILTERABLE_CHANNELS: UserFacingMessageChannel[] = [
  "gmail",
  "outlook",
  "customEmail",
  "instagramDm",
  "instagramComments",
  "facebookDm",
  "facebookComments",
  "tiktok",
  "attentive",
  "sms",
  "mobileMessage",
  "postscript",
  "redoChat",
  "voice",
  "contactForm",
];

/**
 * Human-readable labels for filterable channels.
 */
const FilterableChannelReadable: Record<UserFacingMessageChannel, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
  customEmail: "Email",
  instagramDm: "Instagram DM",
  instagramComments: "Instagram Comments",
  facebookDm: "Facebook DM",
  facebookComments: "Facebook Comments",
  tiktok: "TikTok",
  attentive: "Attentive",
  sms: "SMS",
  mobileMessage: "Mobile Message",
  postscript: "Postscript",
  redoChat: "Redo Chat",
  voice: "Voice",
  contactForm: "Contact Form",
  internalNote: "Internal Note",
};

/**
 * Channels filter uses MessageChannel values to match what's stored
 * in OpenSearch and returned by the API.
 *
 * All channels are always shown, regardless of which integrations the merchant
 * has connected. Merchants may want to filter by a channel they previously used
 * or are about to connect, so we never hide channels based on connection state.
 */
export function createChannelsAdvancedFilter(): ArrayStringTableFilter<MessageChannel> {
  const channels = ALL_FILTERABLE_CHANNELS;

  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: ChannelsIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: supportTicketFilterData[SupportTicketFilterNames.CHANNELS],
    itemLabel: "channel",
    itemLabelPlural: "channels",
    values: channels,
    display: (item: MessageChannel) =>
      FilterableChannelReadable[userFacingMessageChannel(item)],
    displayName: "Channels",
    itemGenerator: (
      item: MessageChannel,
    ): TextRedoListItem<MessageChannel> => ({
      id: item,
      value: item,
      type: "text",
      text: FilterableChannelReadable[userFacingMessageChannel(item)],
      leadingItem: {
        type: "icon",
        Icon: MessageChannelIcons[userFacingMessageChannel(item)],
      },
    }),
  };
}
